/**
 * FDM Dashboard Tab Component
 * Shows overview stats, top categories, severity distribution, and recent exceedances
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Plane, AlertTriangle, CheckCircle2, Clock, Activity, ChevronRight 
} from 'lucide-react';
import { getSeverityColor, getSeverityLabel, formatCategory } from './fdmUtils';

export default function FDMDashboardTab({ 
  dashboardStats, 
  exceedances, 
  onNavigateToExceedances 
}) {
  return (
    <div className="space-y-6 mt-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Total Flights</p>
                <p className="text-2xl font-bold text-slate-100">{dashboardStats?.total_flights || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/20">
                <Plane className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Exceedances</p>
                <p className="text-2xl font-bold text-slate-100">{dashboardStats?.total_exceedances || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-orange-500/20">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Clean Flight Rate</p>
                <p className="text-2xl font-bold text-green-400">{dashboardStats?.clean_flight_rate || 100}%</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500/20">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Pending Review</p>
                <p className="text-2xl font-bold text-yellow-400">{dashboardStats?.pending_review || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-yellow-500/20">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Rate (30d)</p>
                <p className="text-2xl font-bold text-slate-100">{dashboardStats?.exceedance_rate_30d || 0}</p>
                <p className="text-xs text-slate-500">per 1000 flights</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-500/20">
                <Activity className="w-5 h-5 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Categories and Severity Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Categories */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg text-slate-100">Top Exceedance Categories</CardTitle>
            <CardDescription>Most frequent exceedance types</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(dashboardStats?.top_categories || []).slice(0, 5).map((cat, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${idx === 0 ? 'bg-red-500' : idx === 1 ? 'bg-orange-500' : 'bg-yellow-500'}`} />
                    <span className="text-sm text-slate-300">{formatCategory(cat.category)}</span>
                  </div>
                  <Badge variant="secondary">{cat.count}</Badge>
                </div>
              ))}
              {(!dashboardStats?.top_categories || dashboardStats.top_categories.length === 0) && (
                <p className="text-slate-500 text-sm text-center py-4">No exceedances recorded yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* By Severity */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg text-slate-100">Exceedances by Severity</CardTitle>
            <CardDescription>Distribution across severity levels</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {['level_5', 'level_4', 'level_3', 'level_2', 'level_1'].map((level) => {
                const count = dashboardStats?.by_severity?.[level] || 0;
                const total = dashboardStats?.total_exceedances || 1;
                const percent = Math.round((count / total) * 100) || 0;
                
                return (
                  <div key={level} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-300">{getSeverityLabel(level)}</span>
                      <span className="text-slate-400">{count}</span>
                    </div>
                    <Progress value={percent} className="h-2" />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Exceedances */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg text-slate-100">Recent Exceedances (Last 7 Days)</CardTitle>
              <CardDescription>{dashboardStats?.recent_exceedances_7d || 0} events detected</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={onNavigateToExceedances}>
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {exceedances.slice(0, 5).map((exc) => (
              <div key={exc.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-700/30 hover:bg-slate-700/50 transition-colors">
                <div className="flex items-center gap-3">
                  <AlertTriangle className={`w-4 h-4 ${exc.severity?.includes('5') || exc.severity?.includes('4') ? 'text-red-400' : 'text-orange-400'}`} />
                  <div>
                    <p className="text-sm font-medium text-slate-200">{formatCategory(exc.category)}</p>
                    <p className="text-xs text-slate-500">{exc.flight_info?.flight_number} • {exc.flight_info?.aircraft_registration}</p>
                  </div>
                </div>
                <Badge className={getSeverityColor(exc.severity)}>{exc.severity?.replace('_', ' ')}</Badge>
              </div>
            ))}
            {exceedances.length === 0 && (
              <p className="text-slate-500 text-sm text-center py-8">No recent exceedances. Seed demo data to see sample events.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
